import { asyncSelectState, initialState } from './state'

import { type Getter, type Setter } from 'jotai'
import {
  type AsyncSelectState,
  type FetchItemByIdFunction,
  type FetchItemsByTermFunction,
  type FetchItemsFunction,
  type OptionEntry,
  type OptionEntryValue,
} from './model'

export const onValueChangeCallback = async (
  get: Getter,
  set: Setter,
  key: string,
  _value: OptionEntryValue | OptionEntryValue[] | undefined,
  fetchItems: FetchItemsFunction,
  fetchItemById: FetchItemByIdFunction,
): Promise<void> => {
  const { status, initialOptions, searchOptions } = get(asyncSelectState(key))

  if (status === 'error') {
    return
  }

  set(asyncSelectState(key), (prev: AsyncSelectState) => ({
    ...prev,
    loading: true,
  }))

  try {
    if (status === 'initial') {
      const { items, totalItems } = await fetchItems()

      const tmp: OptionEntryValue[] = Array.isArray(_value)
        ? [..._value]
        : _value === undefined
          ? []
          : [_value]

      const toAdd = await Promise.all(
        tmp
          .filter((v) => v !== 0)
          .filter(
            (v) =>
              items.find(({ value: optValue }) => optValue === v) === undefined,
          ) // se non fanno parte del set iniziale
          .map(fetchItemById), // loading singolo
      )
      const toAddClean = toAdd.filter((v) => v !== undefined) // filtro i risultati null di getItemById

      set(asyncSelectState(key), (prev: AsyncSelectState) => ({
        ...prev,
        status: 'ready',
        initialOptions:
          toAddClean.length > 0 ? [...toAddClean, ...items] : items,
        initialOptionsCount: totalItems,
      }))
    }

    if (status === 'ready') {
      const tmp: OptionEntryValue[] = Array.isArray(_value)
        ? [..._value]
        : _value === undefined
          ? []
          : [_value]

      const toAddFromSearching = tmp
        .filter(
          (v) =>
            initialOptions.find(({ value: optValue }) => optValue === v) ===
            undefined,
        ) // elementi di value non presenti in initialOptions
        .map((v) => searchOptions.find(({ value: optValue }) => optValue === v)) // prelevo da serchingOptions
        .filter((v) => v !== undefined)

      const tmp2: OptionEntry[] = [...initialOptions, ...searchOptions]

      const tmp3: OptionEntryValue[] = Array.isArray(_value)
        ? [..._value]
        : _value === undefined
          ? []
          : [_value]

      const toAddFromApi = await Promise.all(
        tmp3
          .filter((v) => v !== 0)
          .filter((v) => !tmp2.some((a) => a.value === v)) // elementi di value non presenti in initialOptions concat searchingOptions
          .map(fetchItemById), //  prelevo da API
      )

      const toAddFromApiClean = toAddFromApi.filter((v) => v !== undefined)

      const newOptions = [
        ...toAddFromApiClean,
        ...toAddFromSearching,
        ...initialOptions,
      ]
      //  dedupe
      const dedupedOptions = [
        ...new Map(newOptions.map((item) => [item.value, item])).values(),
      ]

      set(asyncSelectState(key), (prev: AsyncSelectState) => ({
        ...prev,
        initialOptions: dedupedOptions,
      }))
    }
  } catch (error) {
    console.error('### error', error)

    set(asyncSelectState(key), (prev: AsyncSelectState) => ({
      ...prev,
      status: 'error',
    }))
  }

  set(asyncSelectState(key), (prev: AsyncSelectState) => ({
    ...prev,
    loading: false,
  }))
}

export const searchItemsCallback = async (
  _: Getter,
  set: Setter,
  key: string,
  searchTerm: string,
  fetchItemsByTerm: FetchItemsByTermFunction,
): Promise<void> => {
  set(asyncSelectState(key), (prev: AsyncSelectState) => ({
    ...prev,
    loading: true,
    searching: true,
  }))

  try {
    const { items, totalItems } = await fetchItemsByTerm(searchTerm)

    set(asyncSelectState(key), (prev: AsyncSelectState) => ({
      ...prev,
      searchOptions: items,
      searchOptionsCount: totalItems,
    }))
  } catch (error) {
    console.error('### error', error)

    set(asyncSelectState(key), (prev: AsyncSelectState) => ({
      ...prev,
      status: 'error',
    }))
  }

  set(asyncSelectState(key), (prev: AsyncSelectState) => ({
    ...prev,
    loading: false,
  }))
}

export const resetSearchCallback = (
  _: Getter,
  set: Setter,
  key: string,
): void => {
  set(asyncSelectState(key), (prev: AsyncSelectState) => ({
    ...prev,
    searching: false,
    searchOptionsCount: 0,
  }))
}

export const resetCallback = (_: Getter, set: Setter, key: string): void => {
  set(asyncSelectState(key), initialState)
}
